import { useState, useEffect, useCallback } from 'react';
import {
  Bell,
  Plus,
  Search,
  Trash2,
  Edit,
  Pause,
  Play,
  RefreshCw,
  Calendar,
  Clock,
  User,
  Phone,
  Music,
  Brain,
  Filter,
  MoreHorizontal,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Zap,
  History,
  BarChart3,
  Building2
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/voip/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@easyflow/ui-frontend/components/ui/dialog';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import apiClient from '@/lib/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@easyflow/ui-frontend/components/ui/dropdown-menu';
import { getErrorMessage } from '@/lib/utils';


interface WakeupCall {
  id: string;
  extension_id: string;
  extension_number: string;
  ext_number?: string;
  ext_display_name?: string;
  guest_name: string;
  scheduled_at: string;
  wakeup_type: 'audio' | 'ai';
  audio_file_path: string;
  ai_agent_id: string;
  ai_agent_name?: string;
  language: string;
  status: 'active' | 'suspended' | 'completed' | 'failed' | 'cancelled';
  retry_count: number;
  max_retries: number;
  last_attempt_at: string;
  answered_at: string;
  failed_reason: string;
  recurrence: string;
  notes: string;
  created_by_email: string;
  created_at: string;
  ai_prompt_override?: string;
}

interface WakeupCallLog {
  id: string;
  wakeup_call_id: string;
  attempt_number: number;
  call_uuid: string | null;
  started_at: string | null;
  answered_at: string | null;
  ended_at: string | null;
  duration_seconds: number | null;
  result: string;
  hangup_cause: string | null;
  recording_path: string | null;
  notes: string | null;
  created_at: string;
}

interface Extension {
  id: string;
  extension: string;
  display_name: string;
  room_number?: string | null;
}

interface AIAgent {
  id: string;
  name: string;
}

interface SoundFile {
  name: string;
  path: string;
}

interface Stats {
  active: number;
  suspended: number;
  completed: number;
  failed: number;
  today: number;
  audio_type: number;
  ai_type: number;
}

export default function WakeupCalls() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [wakeupCalls, setWakeupCalls] = useState<WakeupCall[]>([]);
  const [extensions, setExtensions] = useState<Extension[]>([]);
  const [aiAgents, setAiAgents] = useState<AIAgent[]>([]);
  const [sounds, setSounds] = useState<SoundFile[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCall, setEditingCall] = useState<WakeupCall | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [selectedCallLogs, setSelectedCallLogs] = useState<WakeupCallLog[]>([]);
  const [selectedCallForLogs, setSelectedCallForLogs] = useState<WakeupCall | null>(null);
  const [triggeringId, setTriggeringId] = useState<string | null>(null);
  const [selectMode, setSelectMode] = useState<'extension' | 'room'>('extension');

  const [formData, setFormData] = useState({
    extension_id: '',
    extension_number: '',
    guest_name: '',
    scheduled_date: '',
    scheduled_time: '',
    wakeup_type: 'audio' as 'audio' | 'ai',
    audio_file_path: '',
    ai_agent_id: '',
    ai_prompt_override: '',
    language: 'it',
    max_retries: 4,
    recurrence: 'none',
    notes: ''
  });

  const generateDefaultPrompt = (guestName: string, time: string, language: string) => {
    const hour = parseInt(time.split(':')[0]!, 10);
    let greeting = 'Buongiorno';
    if (hour >= 21 || hour < 6) greeting = 'Buonanotte';
    else if (hour >= 13) greeting = 'Buonasera';

    const name = guestName?.trim() || 'Ospite';

    if (language === 'en') {
      const greetingEn = hour >= 21 || hour < 6 ? 'Good evening' : hour >= 13 ? 'Good afternoon' : 'Good morning';
      return `You are a professional hotel concierge AI assistant making a wake-up call. Greet the guest warmly: "${greetingEn}, ${name}. This is your scheduled wake-up call at ${time}." Be polite, friendly and ask if they need anything else. Keep the conversation brief and professional.`;
    }

    return `Sei un Agent AI professionale di un hotel che effettua una chiamata sveglia. Saluta cordialmente l'ospite: "${greeting} ${name}, questa è la sua sveglia programmata per le ${time}." Sii cortese, amichevole e chiedi se ha bisogno di qualcos'altro. Mantieni la conversazione breve e professionale.`;
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const tenantSlug = user?.tenantSlug || 'demo';
      
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter && typeFilter !== 'all') params.append('wakeup_type', typeFilter);
      if (searchTerm) params.append('search', searchTerm);

      const [callsRes, statsRes, extsRes, agentsRes, soundsRes] = await Promise.all([
        apiClient.get<{ wakeup_calls?: WakeupCall[] }>(`/wakeup-calls?${params.toString()}`),
        apiClient.get(`/wakeup-calls/stats`),
        apiClient.get<{ items?: Extension[]; extensions?: Extension[] } | Extension[]>(`/extensions`),
        apiClient.get<{ agents?: AIAgent[] } | AIAgent[]>(`/ai/voice-agents`),
        apiClient.get<{ files?: Array<{ filename: string; path: string }> }>(`/audio/list?dir=wakeup`)
      ]);

      if (callsRes?.success) setWakeupCalls((callsRes.data)?.wakeup_calls || []);
      if (statsRes?.success) setStats(statsRes.data as Stats);
      if (extsRes?.success) {
        const extData = extsRes.data;
        setExtensions(Array.isArray(extData) ? extData : (extData?.items || extData?.extensions || []));
      }
      if (agentsRes?.success) {
        const agentData = agentsRes.data;
        setAiAgents(Array.isArray(agentData) ? agentData : (agentData?.agents || []));
      }
      if (soundsRes?.success) {
        const files = (soundsRes.data)?.files || [];
        setSounds(files.map((f) => ({ name: f.filename, path: f.path })));
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, typeFilter, user?.tenantSlug]);

  useEffect(() => {
    loadData();
  }, [statusFilter, typeFilter, loadData]);

  const openCreateModal = () => {
    setEditingCall(null);
    setSelectMode('extension');
    const now = new Date();
    now.setHours(now.getHours() + 1);
    setFormData({
      extension_id: '',
      extension_number: '',
      guest_name: '',
      scheduled_date: now.toISOString().split('T')[0]!,
      scheduled_time: '07:00',
      wakeup_type: 'audio',
      audio_file_path: '',
      ai_agent_id: '',
      ai_prompt_override: '',
      language: 'it',
      max_retries: 4,
      recurrence: 'none',
      notes: ''
    });
    setIsModalOpen(true);
  };

  const openEditModal = (call: WakeupCall) => {
    setEditingCall(call);
    setSelectMode('extension');
    const scheduledDate = new Date(call.scheduled_at);
    setFormData({
      extension_id: call.extension_id || '',
      extension_number: call.extension_number || '',
      guest_name: call.guest_name || '',
      scheduled_date: scheduledDate.toISOString().split('T')[0]!,
      scheduled_time: scheduledDate.toTimeString().slice(0, 5),
      wakeup_type: call.wakeup_type,
      audio_file_path: call.audio_file_path || '',
      ai_agent_id: call.ai_agent_id || '',
      ai_prompt_override: call.ai_prompt_override || '',
      language: call.language || 'it',
      max_retries: call.max_retries || 4,
      recurrence: call.recurrence || 'none',
      notes: call.notes || ''
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const tenantSlug = user?.tenantSlug || 'demo';
      
      const scheduledDateTime = new Date(`${formData.scheduled_date}T${formData.scheduled_time}`);
      const now = new Date();
      
      if (scheduledDateTime <= now) {
        toast({ 
          title: 'Errore', 
          description: 'Non puoi impostare una sveglia nel passato. Seleziona una data/ora futura.', 
          variant: 'destructive' 
        });
        return;
      }
      
      const scheduled_at = scheduledDateTime.toISOString();
      
      const payload = {
        extension_id: formData.extension_id || null,
        extension_number: formData.extension_number,
        guest_name: formData.guest_name,
        scheduled_at,
        wakeup_type: formData.wakeup_type,
        audio_file_path: formData.wakeup_type === 'audio' ? formData.audio_file_path : null,
        ai_agent_id: formData.wakeup_type === 'ai' ? formData.ai_agent_id : null,
        ai_prompt_override: formData.wakeup_type === 'ai' ? (formData.ai_prompt_override || null) : null,
        language: formData.language,
        max_retries: formData.max_retries,
        recurrence: formData.recurrence,
        notes: formData.notes
      };

      if (editingCall && !editingCall.id) {
        toast({ title: 'Errore', description: 'Stato modifica inconsistente: id mancante', variant: 'destructive' });
        return;
      }
      if (editingCall?.id) {
        await apiClient.patch(`/wakeup-calls/${editingCall.id}`, payload);
        toast({ title: 'Successo', description: 'Sveglia aggiornata' });
      } else {
        await apiClient.post(`/wakeup-calls`, payload);
        toast({ title: 'Successo', description: 'Sveglia creata' });
      }

      setIsModalOpen(false);
      setEditingCall(null);
      loadData();
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error) || 'Errore nel salvataggio', variant: 'destructive' });
    }
  };

  const handleSuspend = async (id: string) => {
    try {
      const tenantSlug = user?.tenantSlug || 'demo';
      await apiClient.patch(`/wakeup-calls/${id}/suspend`);
      toast({ title: 'Successo', description: 'Sveglia sospesa' });
      loadData();
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    }
  };

  const handleActivate = async (id: string) => {
    try {
      const tenantSlug = user?.tenantSlug || 'demo';
      await apiClient.patch(`/wakeup-calls/${id}/activate`);
      toast({ title: 'Successo', description: 'Sveglia riattivata' });
      loadData();
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Sei sicuro di voler eliminare questa sveglia?')) return;
    try {
      const tenantSlug = user?.tenantSlug || 'demo';
      await apiClient.delete(`/wakeup-calls/${id}`);
      toast({ title: 'Successo', description: 'Sveglia eliminata' });
      loadData();
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    }
  };

  const handleTrigger = async (id: string) => {
    setTriggeringId(id);
    try {
      const tenantSlug = user?.tenantSlug || 'demo';
      const result = await apiClient.post(`/wakeup-calls/${id}/trigger`);
      if (result?.success) {
        toast({ title: 'Successo', description: 'Chiamata sveglia avviata' });
      } else {
        toast({ title: 'Errore', description: result?.message || 'Errore nell\'avvio', variant: 'destructive' });
      }
      loadData();
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setTriggeringId(null);
    }
  };

  const handleViewLogs = async (call: WakeupCall) => {
    setSelectedCallForLogs(call);
    try {
      const tenantSlug = user?.tenantSlug || 'demo';
      const result = await apiClient.get<{ logs?: WakeupCallLog[] }>(`/wakeup-calls/${call.id}/logs`);
      if (result?.success) {
        setSelectedCallLogs((result.data)?.logs || []);
      }
      setLogsModalOpen(true);
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    }
  };

  const getLogResultBadge = (result: string) => {
    switch (result) {
      case 'answered':
        return <Badge className="bg-green-500/20 text-green-400">Risposta</Badge>;
      case 'no_answer':
        return <Badge className="bg-yellow-500/20 text-yellow-400">Nessuna risposta</Badge>;
      case 'busy':
        return <Badge className="bg-orange-500/20 text-orange-400">Occupato</Badge>;
      case 'failed':
        return <Badge className="bg-red-500/20 text-red-400">Fallito</Badge>;
      case 'pending':
        return <Badge className="bg-blue-500/20 text-blue-400">In corso</Badge>;
      default:
        return <Badge>{result}</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Attiva</Badge>;
      case 'suspended':
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Sospesa</Badge>;
      case 'completed':
        return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Completata</Badge>;
      case 'failed':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Fallita</Badge>;
      case 'cancelled':
        return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">Annullata</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getTypeBadge = (type: string) => {
    if (type === 'ai') {
      return <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30"><Brain className="w-3 h-3 mr-1" />AI</Badge>;
    }
    return <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30"><Music className="w-3 h-3 mr-1" />Audio</Badge>;
  };

  const formatDateTime = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('it-IT', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Bell className="w-6 h-6 text-cyan-500" />
            Sveglie Hotel
          </h1>
          <p className="text-gray-600 dark:text-gray-400">Gestisci le sveglie per gli ospiti</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
          <Button onClick={openCreateModal} className="bg-gradient-to-r from-cyan-500 to-purple-500">
            <Plus className="w-4 h-4 mr-2" />
            Nuova Sveglia
          </Button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <Card className="bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.active || 0}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Attive</div>
            </CardContent>
          </Card>
          <Card className="bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats.suspended || 0}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Sospese</div>
            </CardContent>
          </Card>
          <Card className="bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.completed || 0}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Completate</div>
            </CardContent>
          </Card>
          <Card className="bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.failed || 0}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Fallite</div>
            </CardContent>
          </Card>
          <Card className="bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">{stats.today || 0}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Oggi</div>
            </CardContent>
          </Card>
          <Card className="bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">{stats.audio_type || 0}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Audio</div>
            </CardContent>
          </Card>
          <Card className="bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{stats.ai_type || 0}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">AI</div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className="bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4 justify-between">
            <div className="flex gap-2 flex-1">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-gray-400" />
                <Input
                  placeholder="Cerca camera o ospite..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadData()}
                  className="pl-10 bg-gray-100 dark:bg-gray-900/50 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px] bg-gray-100 dark:bg-gray-900/50 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
                  <SelectValue placeholder="Stato" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="active">Attive</SelectItem>
                  <SelectItem value="suspended">Sospese</SelectItem>
                  <SelectItem value="completed">Completate</SelectItem>
                  <SelectItem value="failed">Fallite</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[140px] bg-gray-100 dark:bg-gray-900/50 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="audio">Audio</SelectItem>
                  <SelectItem value="ai">AI</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left p-3 text-gray-700 dark:text-gray-400 font-medium">Camera</th>
                  <th className="text-left p-3 text-gray-700 dark:text-gray-400 font-medium">Ospite</th>
                  <th className="text-left p-3 text-gray-700 dark:text-gray-400 font-medium">Data/Ora</th>
                  <th className="text-left p-3 text-gray-700 dark:text-gray-400 font-medium">Tipo</th>
                  <th className="text-left p-3 text-gray-700 dark:text-gray-400 font-medium">Stato</th>
                  <th className="text-left p-3 text-gray-700 dark:text-gray-400 font-medium">Tentativi</th>
                  <th className="text-right p-3 text-gray-700 dark:text-gray-400 font-medium">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {wakeupCalls.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center p-8 text-gray-500">
                      {loading ? 'Caricamento...' : 'Nessuna sveglia trovata'}
                    </td>
                  </tr>
                ) : (
                  wakeupCalls.map((call) => (
                    <tr key={call.id} className="border-b border-gray-200 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                          <span className="text-gray-900 dark:text-white font-medium">
                            {call.ext_number || call.extension_number}
                          </span>
                        </div>
                        {call.ext_display_name && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">{call.ext_display_name}</div>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          <span className="text-gray-900 dark:text-white">{call.guest_name || '-'}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          <span className="text-gray-900 dark:text-white">{formatDateTime(call.scheduled_at)}</span>
                        </div>
                      </td>
                      <td className="p-3">{getTypeBadge(call.wakeup_type)}</td>
                      <td className="p-3">{getStatusBadge(call.status)}</td>
                      <td className="p-3">
                        <span className="text-gray-700 dark:text-gray-300">{call.retry_count}/{call.max_retries}</span>
                      </td>
                      <td className="p-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditModal(call)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Modifica
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleViewLogs(call)}>
                              <History className="w-4 h-4 mr-2" />
                              Storico Chiamate
                            </DropdownMenuItem>
                            {call.status === 'active' && (
                              <>
                                <DropdownMenuItem 
                                  onClick={() => handleTrigger(call.id)}
                                  disabled={triggeringId === call.id}
                                >
                                  <Zap className="w-4 h-4 mr-2" />
                                  {triggeringId === call.id ? 'Avvio...' : 'Esegui Ora'}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleSuspend(call.id)}>
                                  <Pause className="w-4 h-4 mr-2" />
                                  Sospendi
                                </DropdownMenuItem>
                              </>
                            )}
                            {call.status === 'suspended' && (
                              <DropdownMenuItem onClick={() => handleActivate(call.id)}>
                                <Play className="w-4 h-4 mr-2" />
                                Riattiva
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleDelete(call.id)} className="text-red-400">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Elimina
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white">
              {editingCall ? 'Modifica Sveglia' : 'Nuova Sveglia'}
            </DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400">
              {editingCall ? 'Modifica i dettagli della sveglia' : 'Crea una nuova sveglia per un ospite'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 overflow-y-auto flex-1 pr-2">
            {/* Toggle per selezione Estensione / Stanza */}
            <div className="flex items-center gap-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-900/30 border border-gray-200 dark:border-gray-700">
              <span className="text-sm text-gray-600 dark:text-gray-400">Seleziona per:</span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={selectMode === 'extension' ? 'default' : 'outline'}
                  onClick={() => {
                    setSelectMode('extension');
                    setFormData({ ...formData, extension_id: '', extension_number: '' });
                  }}
                  className={selectMode === 'extension' ? 'bg-cyan-600 hover:bg-cyan-700' : ''}
                >
                  <Phone className="w-4 h-4 mr-1" />
                  Estensione
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={selectMode === 'room' ? 'default' : 'outline'}
                  onClick={() => {
                    setSelectMode('room');
                    setFormData({ ...formData, extension_id: '', extension_number: '' });
                  }}
                  className={selectMode === 'room' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                >
                  <Building2 className="w-4 h-4 mr-1" />
                  N. Stanza
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700 dark:text-gray-300">
                  {selectMode === 'extension' ? 'Estensione *' : 'Camera *'}
                </Label>
                <Select
                  value={formData.extension_id}
                  onValueChange={(value) => {
                    const ext = extensions.find(e => e.id === value);
                    setFormData({
                      ...formData,
                      extension_id: value,
                      extension_number: ext?.extension || ''
                    });
                  }}
                >
                  <SelectTrigger className="bg-gray-100 dark:bg-gray-900/50 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
                    <SelectValue placeholder="Seleziona..." />
                  </SelectTrigger>
                  <SelectContent>
                    {selectMode === 'extension' ? (
                      Array.isArray(extensions) && extensions.map((ext) => (
                        <SelectItem key={ext.id} value={ext.id || 'unknown'}>
                          {ext.extension} - {ext.display_name}
                        </SelectItem>
                      ))
                    ) : (
                      Array.isArray(extensions) && extensions
                        .filter(ext => ext.room_number)
                        .map((ext) => (
                          <SelectItem key={ext.id} value={ext.id || 'unknown'}>
                            Camera {ext.room_number} ({ext.extension})
                          </SelectItem>
                        ))
                    )}
                    {selectMode === 'room' && extensions.filter(ext => ext.room_number).length === 0 && (
                      <div className="px-2 py-3 text-sm text-gray-500 text-center">
                        Nessuna estensione con camera configurata
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 dark:text-gray-300">Nome Ospite</Label>
                <Input
                  value={formData.guest_name}
                  onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })}
                  placeholder="Mario Rossi"
                  className="bg-gray-100 dark:bg-gray-900/50 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700 dark:text-gray-300">Data *</Label>
                <Input
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                  className="bg-gray-100 dark:bg-gray-900/50 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 dark:text-gray-300">Ora *</Label>
                <Input
                  type="time"
                  value={formData.scheduled_time}
                  onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                  className="bg-gray-100 dark:bg-gray-900/50 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700 dark:text-gray-300">Tipo Sveglia *</Label>
              <Select
                value={formData.wakeup_type}
                onValueChange={(value: 'audio' | 'ai') => setFormData({ ...formData, wakeup_type: value })}
              >
                <SelectTrigger className="bg-gray-100 dark:bg-gray-900/50 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="audio">
                    <div className="flex items-center gap-2">
                      <Music className="w-4 h-4" />
                      Audio - File MP3
                    </div>
                  </SelectItem>
                  <SelectItem value="ai">
                    <div className="flex items-center gap-2">
                      <Brain className="w-4 h-4" />
                      Agent AI
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.wakeup_type === 'audio' && (
              <div className="space-y-2">
                <Label className="text-gray-700 dark:text-gray-300">File Audio *</Label>
                <Select
                  value={formData.audio_file_path}
                  onValueChange={(value) => setFormData({ ...formData, audio_file_path: value })}
                >
                  <SelectTrigger className="bg-gray-100 dark:bg-gray-900/50 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
                    <SelectValue placeholder="Seleziona audio..." />
                  </SelectTrigger>
                  <SelectContent>
                    {sounds.map((sound) => (
                      <SelectItem key={sound.path} value={sound.path}>
                        {sound.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {formData.wakeup_type === 'ai' && (
              <>
                <div className="space-y-2">
                  <Label className="text-gray-700 dark:text-gray-300">AI Agent *</Label>
                  <Select
                    value={formData.ai_agent_id}
                    onValueChange={(value) => setFormData({ ...formData, ai_agent_id: value })}
                  >
                    <SelectTrigger className="bg-gray-100 dark:bg-gray-900/50 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
                      <SelectValue placeholder="Seleziona agent..." />
                    </SelectTrigger>
                    <SelectContent>
                      {aiAgents.map((agent) => (
                          <SelectItem key={agent.id} value={agent.id}>
                            {agent.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-gray-700 dark:text-gray-300">Prompt AI (opzionale)</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-xs text-cyan-600 dark:text-cyan-400 h-auto py-0"
                      onClick={() => {
                        const defaultPrompt = generateDefaultPrompt(
                          formData.guest_name,
                          formData.scheduled_time,
                          formData.language
                        );
                        setFormData({ ...formData, ai_prompt_override: defaultPrompt });
                      }}
                    >
                      Genera prompt predefinito
                    </Button>
                  </div>
                  <Textarea
                    value={formData.ai_prompt_override}
                    onChange={(e) => setFormData({ ...formData, ai_prompt_override: e.target.value })}
                    placeholder="Lascia vuoto per usare il prompt dell'agent selezionato, oppure clicca 'Genera prompt predefinito' per un prompt contestuale con saluto e nome ospite..."
                    className="bg-gray-100 dark:bg-gray-900/50 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm"
                    rows={4}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Se compilato, sovrascrive il prompt di sistema dell'agent AI per questa chiamata. Include automaticamente saluto basato sull'orario e nome ospite.
                  </p>
                </div>
              </>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700 dark:text-gray-300">Lingua</Label>
                <Select
                  value={formData.language}
                  onValueChange={(value) => setFormData({ ...formData, language: value })}
                >
                  <SelectTrigger className="bg-gray-100 dark:bg-gray-900/50 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="it">Italiano</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="de">Deutsch</SelectItem>
                    <SelectItem value="fr">Français</SelectItem>
                    <SelectItem value="es">Español</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 dark:text-gray-300">Max Tentativi</Label>
                <Select
                  value={formData.max_retries.toString()}
                  onValueChange={(value) => setFormData({ ...formData, max_retries: parseInt(value) })}
                >
                  <SelectTrigger className="bg-gray-100 dark:bg-gray-900/50 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1</SelectItem>
                    <SelectItem value="2">2</SelectItem>
                    <SelectItem value="3">3</SelectItem>
                    <SelectItem value="4">4</SelectItem>
                    <SelectItem value="5">5</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700 dark:text-gray-300">Ricorrenza</Label>
              <Select
                value={formData.recurrence}
                onValueChange={(value) => setFormData({ ...formData, recurrence: value })}
              >
                <SelectTrigger className="bg-gray-100 dark:bg-gray-900/50 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Singola</SelectItem>
                  <SelectItem value="daily">Giornaliera</SelectItem>
                  <SelectItem value="weekdays">Lun-Ven</SelectItem>
                  <SelectItem value="weekends">Sab-Dom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700 dark:text-gray-300">Note</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Note aggiuntive..."
                className="bg-gray-100 dark:bg-gray-900/50 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleSubmit} className="bg-gradient-to-r from-cyan-500 to-purple-500">
              {editingCall ? 'Salva Modifiche' : 'Crea Sveglia'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={logsModalOpen} onOpenChange={setLogsModalOpen}>
        <DialogContent className="max-w-2xl bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white flex items-center gap-2">
              <History className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
              Storico Chiamate
            </DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400">
              {selectedCallForLogs && (
                <span>
                  Camera {selectedCallForLogs.ext_number || selectedCallForLogs.extension_number}
                  {selectedCallForLogs.guest_name && ` - ${selectedCallForLogs.guest_name}`}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-96 overflow-y-auto">
            {selectedCallLogs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Nessun tentativo di chiamata registrato
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left p-2 text-gray-600 dark:text-gray-400 text-sm">#</th>
                    <th className="text-left p-2 text-gray-600 dark:text-gray-400 text-sm">Data/Ora</th>
                    <th className="text-left p-2 text-gray-600 dark:text-gray-400 text-sm">Risultato</th>
                    <th className="text-left p-2 text-gray-600 dark:text-gray-400 text-sm">Durata</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedCallLogs.map((log) => (
                    <tr key={log.id} className="border-b border-gray-200 dark:border-gray-700/50">
                      <td className="p-2 text-gray-700 dark:text-gray-300">{log.attempt_number}</td>
                      <td className="p-2 text-gray-700 dark:text-gray-300">{formatDateTime(log.started_at)}</td>
                      <td className="p-2">{getLogResultBadge(log.result)}</td>
                      <td className="p-2 text-gray-700 dark:text-gray-300">
                        {log.duration_seconds ? `${log.duration_seconds}s` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setLogsModalOpen(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
